import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Award, Image as ImageIcon, FileDown, Share2, Link as LinkIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "../lib/api";
import { useApp } from "../contexts/AppContext";

export default function DiplomaView() {
  const { id } = useParams();
  const { lang } = useApp();
  const pt = lang !== "en";
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/diploma/${id}/info`, { params: { lang } })
      .then((r) => setInfo(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id, lang]);

  const shareUrl = `${window.location.origin}/diploma/view/${id}`;
  const imgUrl = `${API_BASE}/diploma/${id}/image?lang=${lang}`;
  const pdfUrl = `${API_BASE}/diploma/${id}?lang=${lang}`;
  const waText = encodeURIComponent((pt ? `🎄 Vê o meu diploma do NATALMÁTICO! ` : `🎄 Check out my X-MATHS Challenge diploma! `) + shareUrl);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(pt ? "Link copiado!" : "Link copied!");
    } catch (_) {
      const ta = document.createElement("textarea");
      ta.value = shareUrl; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
      toast.success(pt ? "Link copiado!" : "Link copied!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="diploma-view-loading">
        <Loader2 className="animate-spin text-[#FF6B6B]" size={32} />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6" data-testid="diploma-view-notfound">
        <Award size={48} className="text-[#0B1D3A]/30 mb-4" />
        <h1 className="font-fredoka text-2xl font-bold text-[#0B1D3A]">{pt ? "Diploma não encontrado" : "Diploma not found"}</h1>
        <p className="font-nunito text-[#0B1D3A]/60 mt-2">{pt ? "Este link pode estar incorreto." : "This link may be incorrect."}</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="diploma-view-page">
      <div className="text-center mb-6">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FFD93D]/70 border border-[#FFD93D] text-[#0B1D3A] font-nunito font-bold text-sm">
          <Award size={16} /> {pt ? "Diploma de Participação" : "Certificate of Participation"}
        </span>
        <h1 className="font-fredoka text-3xl md:text-4xl font-bold text-[#0B1D3A] mt-4" data-testid="diploma-view-name">{info?.name}</h1>
        {info?.quiz_name && <p className="font-nunito text-[#0B1D3A]/70 mt-1">{info.quiz_name}</p>}
      </div>

      <img src={imgUrl} alt="diploma" className="w-full rounded-3xl border border-[#0B1D3A]/10 shadow-xl mb-6" data-testid="diploma-view-image" />

      <div className="grid grid-cols-2 gap-3 mb-3">
        <a href={imgUrl} target="_blank" rel="noreferrer" data-testid="diploma-view-png"
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#0B1D3A] text-white font-fredoka font-bold py-3 hover:bg-[#13294f] transition-colors">
          <ImageIcon size={18} /> {pt ? "Imagem (PNG)" : "Image (PNG)"}
        </a>
        <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="diploma-view-pdf"
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#005F73] text-white font-fredoka font-bold py-3 hover:bg-[#00485a] transition-colors">
          <FileDown size={18} /> PDF
        </a>
      </div>
      <a href={`https://wa.me/?text=${waText}`} target="_blank" rel="noreferrer" data-testid="diploma-view-whatsapp"
        className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-[#25D366] text-white font-fredoka font-bold py-3 text-lg hover:bg-[#1eb957] transition-colors mb-3">
        <Share2 size={18} /> {pt ? "Partilhar no WhatsApp" : "Share on WhatsApp"}
      </a>
      <button onClick={copyLink} data-testid="diploma-view-copy"
        className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-[#0B1D3A]/15 text-[#0B1D3A] font-bold py-3 hover:bg-[#0B1D3A]/5 transition-colors">
        <LinkIcon size={18} /> {pt ? "Copiar link" : "Copy link"}
      </button>
    </div>
  );
}
